import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import memoize from 'memoize-one'
import moment from 'moment-timezone'

import reduce from 'lodash/reduce'
import map from 'lodash/map'
import includes from 'lodash/includes'
import orderBy from 'lodash/orderBy'
import filter from 'lodash/filter'
import startCase from 'lodash/startCase'
import remove from 'lodash/remove'
import isEmpty from 'lodash/isEmpty'
import lowerCase from 'lodash/lowerCase'
import forEach from 'lodash/forEach'
import uniq from 'lodash/uniq'
import concat from 'lodash/concat'
import deburr from 'lodash/deburr'
import round from 'lodash/round'
import take from 'lodash/take'
import difference from 'lodash/difference'

import HarvestValidateDayTable from './HarvestValidateDayTable.js'
import { setSelectedDayAndFarmForTable, getHarvestData,
  nextLoading, recordsToValidate, validateDay,
  subscribeValidatedDays, unsubscribeValidatedDays } from './redux/harvestActions'
import { makeXlsxFile } from '../FileUtils/redux/fileUtilsActions.js'
import { daysSelector } from '../../utils'

const columns = []
const defaultColumns = ['rut', 'nombre', 'apellido']
const lastColumns = ['validado']

// these are the first columns in table
for (let key of defaultColumns) {
  columns.push({ name: key, label: lowerCase(key), options: { display: 'true', viewColumns: true } })
}

const _data = memoize((harvestRecords, companyId, sites, formats, farms, qualities, selectedDay, selectedFarm) => reduce(harvestRecords, (result, harvestRecord, id) => {
  // include records that are valid, from the company, non pending and not in payment process
  if (harvestRecord.deleted || id.indexOf(companyId) < 0 || harvestRecord.pending || harvestRecord.payment) {
    return result
  }

  // get data based in ids for printing in table
  let site = filter(sites, site => site.id === harvestRecord.site)
  site = isEmpty(site) ? 'n/d' : site[0].name
  let format = filter(formats, (format) => format.id === harvestRecord.format.id)
  format = isEmpty(format) ? 'n/d' : format[0].name
  let quality = filter(qualities, (q) => q.id === harvestRecord.quality)
  quality = isEmpty(quality) ? 'n/d' : quality[0].name
  let farm = filter(farms, (farm) => includes(farm.sites, `site/${harvestRecord.site}`))[0]

  // include if selected day is in range
  const recordDay = moment(harvestRecord.date).tz('America/Santiago').format('YYYY-MM-DD')
  if (!(recordDay >= selectedDay.from && recordDay <= selectedDay.to)) {
    return result
  }

  // include if selected farm is todos or the record farm
  if (selectedFarm && selectedFarm.id && selectedFarm.id !== 'todos' && farm && selectedFarm.id !== farm.id) {
    return result
  }

  const employeeData = result[harvestRecord.employee] || {}

  const harvestRecords = employeeData['harvestRecords'] || { validated: [], nonValidated: [] }

  const relevantHarvestRecord = {
    fecha: moment(harvestRecord.date).tz('America/Santiago').format('DD-MMM h:mm:ss a'),
    cuartel: site,
    predio: farm ? farm.name : 'n/d',
    formato: startCase(format),
    calidad: startCase(quality),
    cantidad: harvestRecord.format.quantity,
    farm: farm ? farm.id : '',
    payment: harvestRecord.payment || false,
    pending: harvestRecord.pending || false,
    validated: harvestRecord.validated || false,
    id
  }

  const summary = employeeData['summary'] || {}

  // separate validated records
  if (harvestRecord.validated) {
    harvestRecords.validated.push(relevantHarvestRecord)
  } else {
    harvestRecords.nonValidated.push(relevantHarvestRecord)
  }

  // compute summary
  if (isEmpty(summary)) {
    summary[`${harvestRecord.format.id}:${harvestRecord.quality}`] = harvestRecord.format.quantity
    // TODO: calculate total to be payed
    // summary.total = 0
  } else {
    const value = summary[`${harvestRecord.format.id}:${harvestRecord.quality}`] || 0
    summary[`${harvestRecord.format.id}:${harvestRecord.quality}`] = value + harvestRecord.format.quantity
    // TODO: calculate total to be payed
    // get labour value for harvesting, multiply and sum
  }

  result[harvestRecord.employee] = {
    harvestRecords,
    summary
  }

  return result
}, {}))

const _columns = memoize((columns) => orderBy(columns, (o) => {
  let idx = defaultColumns.indexOf(o.name)
  if (idx < 0) {
    idx = columns.length
  }
  return idx
}))

const _employeesList = memoize((employeesMemberships) => map(employeesMemberships, (membership) => remove(membership.split('/'), (v, idx) => (idx === 1 || idx === 2)).join('/')))

const _employeesData = memoize((employeesList, employeeRecord) => reduce(employeesList, (result, employeeRecordId) => {
  let employee = employeeRecord[employeeRecordId]

  let relevant = {
    nombre: employee ? deburr(employee.name) : '',
    apellido: employee ? deburr(employee.lastName) : ''
  }

  result[employeeRecordId.split('/')[1]] = relevant
  return result
}, {}))

const _mergeData = memoize((data, employees, formats, qualities) => reduce(data, (result, value, key) => {
  // here we set the values we want to see on the table
  let employee = employees[key] || {}
  value.nombre = employee.nombre || 'n/a'
  value.apellido = employee.apellido || 'n/a'
  value.rut = key
  value.validado = value.harvestRecords.nonValidated.length === 0 ? 'Si' : 'No'

  // set each summary value
  forEach(value.summary, (amount, key) => {
    const format = filter(formats, (format) => format.id === key.split(':')[0])[0].name
    const quality = filter(qualities, (q) => q.id === key.split(':')[1])[0].name
    value[`${format}:${quality}`] = amount

    if (!includes(map(result.columns, (c) => c.name), `${format}:${quality}`)) {
      result.columns.push({ name: `${format}:${quality}`, label: lowerCase(`${format}:${quality}`), options: { display: 'true', viewColumns: true } })
    }
  })

  result.values.push(value)
  return result
}, { values: [], columns: [] }))

const _validatedDays = memoize((harvestValidatedDays, farms) => reduce(farms, (result, farm) => {
  if (harvestValidatedDays[farm.id]) result[farm.id] = harvestValidatedDays[farm.id]
  return result
}, {}))

const _tableValidationComplete = memoize((data) => {
  // data has all the data for the table
  // it is an array of employee info with the harvestrecords key with an arrya of validated and nonValidated harvest records
  const status = reduce(data, (res, value) => {
    if (value && value.harvestRecords) {
      res.nonValidated += value.harvestRecords.nonValidated.length
      res.validated += value.harvestRecords.validated.length
    }
    return res
  }, { nonValidated: 0, validated: 0 })

  let completed = 0
  // don't divide by 0
  if (status.validated || status.nonValidated) {
    completed = round(status.validated / (status.validated + status.nonValidated) * 100)
  }
  return completed
})

const _daysToValidate = memoize((harvestSitesDays, validatedDays, selectedFarm, farms) => {
  if (selectedFarm.id === 'todos') {
    // group all days only if they are present in all farms
    // we do this by taking the difference of all days per farm
    const temp = reduce(validatedDays, (res, value, farm) => {
      const days = take(daysSelector(harvestSitesDays, farms, { id: farm }), 20)
      res[farm] = difference(days, value)
      return res
    }, {})
    // we concat uniq and return
    return reduce(temp, (res, value) => {
      res = concat(res, value)
      return orderBy(uniq(res), null, ['desc'])
    }, [])
  } else {
    const days = take(daysSelector(harvestSitesDays, farms, selectedFarm), 20)

    return difference(days, validatedDays[selectedFarm.id] || [])
  }
})

const mapStateToProps = (state, ownProps) => {
  const locationState = ownProps.location.state
  const sites = locationState.sites
  const formats = locationState.formats
  const farms = locationState.farms
  const qualities = locationState.qualities
  const companyId = state.auth.clientData.activeCompany
  const userAccess = state.main.userData.access
  const { selectedDay, selectedFarm, loading } = state.harvest

  const employeesMemberships = state.employees.companyEmployeesList[`company/${companyId}/list/employees`]
  const employeesList = _employeesList(employeesMemberships)
  const employees = _employeesData(employeesList, state.employees.employeeRecord)
  const { harvestSitesDays, harvestValidatedDays } = state.harvest

  const days = daysSelector(harvestSitesDays, farms, selectedFarm)
  const data = _data(state.harvest.harvestRecord, companyId, sites, formats, farms, qualities, selectedDay, selectedFarm)
  const merged = _mergeData(data, employees, formats, qualities)
  const validatedDays = _validatedDays(harvestValidatedDays, farms)
  const tableValidationComplete = _tableValidationComplete(merged.values)
  const daysToValidate = _daysToValidate(harvestSitesDays, validatedDays, selectedFarm, farms)

  return {
    data: merged.values,
    columns: _columns(uniq(concat(columns, merged.columns, lastColumns))),
    options: { responsive: 'scrollMaxHeight',
      expandableRows: false,
      selectableRows: 'multiple',
      rowsPerPage: 20,
      rowsPerPageOptions: [20, 50, 100]
    },
    sites,
    formats,
    farms,
    employees,
    loading,
    userAccess,
    days,
    validatedDays,
    selectedDay,
    selectedFarm,
    tableValidationComplete,
    daysToValidate
  }
}

const mapDispatchToProps = dispatch => bindActionCreators({
  setSelectedDayAndFarmForTable,
  getHarvestData,
  nextLoading,
  recordsToValidate,
  validateDay,
  subscribeValidatedDays,
  unsubscribeValidatedDays,
  makeXlsxFile
}, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(HarvestValidateDayTable)
